import { useState, useEffect, useCallback } from "react";
import { FloppyDisk, Folder, Trash, X } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export const TemplateBar = ({ builderType, getConfig, applyConfig }) => {
  const { api } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [showSave, setShowSave] = useState(false);
  const [showLoad, setShowLoad] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const fetchTemplates = useCallback(async () => {
    try {
      const resp = await api.get(`/templates?template_type=${builderType}`);
      setTemplates(Array.isArray(resp.data) ? resp.data : resp.data.templates || []);
    } catch (e) { console.error("Failed to fetch templates:", e); }
  }, [api, builderType]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSave = async () => {
    if (!templateName.trim()) {
      toast.error("Enter a template name");
      return;
    }
    setSaving(true);
    try {
      const config = getConfig();
      await api.post("/templates", {
        name: templateName.trim(),
        template_type: builderType,
        config,
      });
      toast.success(`Template "${templateName.trim()}" saved`);
      setTemplateName("");
      setShowSave(false);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleLoad = (template) => {
    applyConfig(template.config);
    setShowLoad(false);
    toast.success(`Template "${template.name}" loaded`);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/templates/${deleteId}`);
      toast.success("Template deleted");
      setDeleteId(null);
      fetchTemplates();
    } catch {
      toast.error("Failed to delete template");
    }
  };

  return (
    <>
      <div className="flex items-center gap-2" data-testid="template-bar">
        <button
          onClick={() => setShowLoad(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-[var(--bg-card)] border border-[var(--border-strong)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-colors"
          data-testid="load-template-button"
        >
          <Folder size={14} />
          Templates{templates.length > 0 ? ` (${templates.length})` : ""}
        </button>
        <button
          onClick={() => setShowSave(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-[var(--accent-subtle)] border border-[#FF4400]/20 text-[#FF4400] hover:bg-[var(--accent-subtle)] transition-colors"
          data-testid="save-template-button"
        >
          <FloppyDisk size={14} />
          Save Template
        </button>
      </div>

      {/* Save Template Dialog */}
      <Dialog open={showSave} onOpenChange={setShowSave}>
        <DialogContent className="bg-[var(--bg-raised)] border-[var(--border-strong)] text-[var(--text-primary)]">
          <DialogHeader>
            <DialogTitle className="text-[var(--text-primary)]">Save as Template</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[var(--text-muted)] -mt-2">Save your current configuration for quick reuse. Max 20 templates.</p>
          <Input
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="e.g., LinkedIn Authority Post Setup"
            className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400]"
            data-testid="template-name-input"
            onKeyDown={(e) => e.key === "Enter" && handleSave()}
            autoFocus
          />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowSave(false)} className="border-[var(--border-strong)] text-[var(--text-secondary)]">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#FF4400] hover:bg-[#FF6633] text-white" data-testid="template-save-confirm">
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Load Template Panel */}
      <AnimatePresence>
        {showLoad && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8 px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="w-full max-w-lg bg-[var(--bg-card)] border border-[var(--border-strong)] rounded-xl overflow-hidden"
              data-testid="template-load-panel"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
                <h2 className="font-['Cormorant_Garamond'] text-lg font-bold text-[var(--text-primary)]">Saved Templates</h2>
                <button onClick={() => setShowLoad(false)} className="p-2 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors" data-testid="close-template-panel">
                  <X size={18} />
                </button>
              </div>

              <div className="p-4">
                {templates.length === 0 ? (
                  <div className="text-center py-8" data-testid="no-templates">
                    <p className="text-[var(--text-muted)] text-sm">No templates saved yet</p>
                    <p className="text-[var(--text-muted)] text-xs mt-1">Configure your form and click "Save Template"</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {templates.map((t) => (
                      <div
                        key={t.template_id}
                        className="flex items-center justify-between px-4 py-3 rounded-lg bg-[var(--bg-base)] border border-[var(--border)] hover:border-[var(--border-strong)] transition-colors group"
                        data-testid={`template-item-${t.template_id}`}
                      >
                        <button
                          onClick={() => handleLoad(t)}
                          className="flex-1 text-left min-w-0"
                          data-testid={`template-load-${t.template_id}`}
                        >
                          <p className="text-sm text-[var(--text-primary)] font-medium truncate">{t.name}</p>
                          <p className="text-[10px] font-['JetBrains_Mono'] text-[var(--text-muted)] mt-0.5">
                            {t.config?.content_type || t.config?.lead_magnet_type || t.builder_type} · {new Date(t.created_at).toLocaleDateString()}
                          </p>
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeleteId(t.template_id); }}
                          className="p-2 rounded-md text-[var(--text-muted)] hover:text-red-400 hover:bg-red-400/10 transition-colors"
                          data-testid={`template-delete-${t.template_id}`}
                          title="Delete template"
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="bg-[var(--bg-raised)] border-[var(--border-strong)] text-[var(--text-primary)]">
          <DialogHeader>
            <DialogTitle className="text-[var(--text-primary)]">Delete template?</DialogTitle>
          </DialogHeader>
          <p className="text-[var(--text-secondary)] text-sm">This cannot be undone.</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteId(null)} className="border-[var(--border-strong)] text-[var(--text-secondary)]">Cancel</Button>
            <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="template-delete-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
